import React from 'react';
import { 
  Shield, Heart, TrendingUp, Award, Activity, Clock,
  CheckCircle, AlertTriangle, Target, Stethoscope, Star,
  Sparkles, Zap, Users, Calendar, Phone, MapPin, Info,
  ChevronDown, ChevronUp, FileText, Pill, Syringe, ThermometerSun,
  Utensils, ShoppingCart, Flame, Leaf, Package, Coffee, Gift,
  Home, Settings, Mail, Bell, Search, Edit, Trash, Plus, Minus,
  Eye, EyeOff, Lock, Unlock, Download, Upload, Share, Bookmark,
  MessageCircle, Send, Image, Video, Music, File, Folder,
  Globe, Link, ExternalLink, Copy, Clipboard, Check, X,
  ArrowRight, ArrowLeft, ArrowUp, ArrowDown, RefreshCw,
  Truck, CreditCard, DollarSign, Percent, Tag, Box
} from 'lucide-react';

/**
 * Structured Content Renderer
 * Renders different section types based on AI-generated content structure
 * Matches the design quality of hardcoded pages like DogDentalCare.jsx
 */

// Extended icon mapping - comprehensive list
const iconComponents = {
  'shield': Shield,
  'heart': Heart,
  'trending-up': TrendingUp,
  'award': Award,
  'activity': Activity,
  'clock': Clock,
  'check-circle': CheckCircle,
  'alert-triangle': AlertTriangle,
  'target': Target,
  'stethoscope': Stethoscope,
  'star': Star,
  'sparkles': Sparkles,
  'zap': Zap,
  'users': Users,
  'calendar': Calendar,
  'phone': Phone,
  'map-pin': MapPin,
  'info': Info,
  'file-text': FileText,
  'pill': Pill,
  'syringe': Syringe,
  'thermometer': ThermometerSun,
  // Additional icons for restaurants, shopping, etc.
  'utensils': Utensils,
  'shopping-cart': ShoppingCart,
  'flame': Flame,
  'leaf': Leaf,
  'package': Package,
  'coffee': Coffee,
  'gift': Gift,
  'home': Home,
  'settings': Settings,
  'mail': Mail,
  'bell': Bell,
  'search': Search,
  'edit': Edit,
  'trash': Trash,
  'plus': Plus,
  'minus': Minus,
  'eye': Eye,
  'eye-off': EyeOff,
  'lock': Lock,
  'unlock': Unlock,
  'download': Download,
  'upload': Upload,
  'share': Share,
  'bookmark': Bookmark,
  'message-circle': MessageCircle,
  'send': Send,
  'image': Image,
  'video': Video,
  'music': Music,
  'file': File,
  'folder': Folder,
  'globe': Globe,
  'link': Link,
  'external-link': ExternalLink,
  'copy': Copy,
  'clipboard': Clipboard,
  'check': Check,
  'x': X,
  'arrow-right': ArrowRight,
  'arrow-left': ArrowLeft,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'refresh': RefreshCw,
  'truck': Truck,
  'credit-card': CreditCard,
  'dollar-sign': DollarSign,
  'percent': Percent,
  'tag': Tag,
  'box': Box
};

// 1. Text Block Section (Intro paragraphs, overview text)
const TextBlock = ({ title, subtitle, paragraphs, centered = true, backgroundColor = 'white', image, imagePosition = 'right', imageAspect = 'landscape' }) => {
  const bgClass = backgroundColor === 'gray' || backgroundColor === 'light' ? 'bg-gray-50' : 'bg-white';
  const hasImage = image && image.trim() !== '';
  
  // Get aspect ratio class based on imageAspect prop
  const getAspectClass = () => {
    switch (imageAspect) {
      case 'square':
        return 'aspect-square';
      case 'portrait':
        return 'aspect-[9/16]';
      case 'landscape':
      default:
        return 'aspect-video';
    }
  };

  // Get grid columns based on image aspect and position
  // Image on left: [image% text%], Image on right: [text% image%]
  const getGridClass = () => {
    const isImageLeft = imagePosition === 'left';
    switch (imageAspect) {
      case 'square':
        // 30% image, 70% text
        return isImageLeft ? 'lg:grid-cols-[30%_70%]' : 'lg:grid-cols-[70%_30%]';
      case 'portrait':
        // 25% image, 75% text
        return isImageLeft ? 'lg:grid-cols-[25%_75%]' : 'lg:grid-cols-[75%_25%]';
      case 'landscape':
      default:
        // 40% image, 60% text
        return isImageLeft ? 'lg:grid-cols-[40%_60%]' : 'lg:grid-cols-[60%_40%]';
    }
  };
  
  return (
    <section className={`${bgClass}`} style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasImage ? (
          <div className={`grid grid-cols-1 ${getGridClass()} gap-2.5 items-center`}>
            {/* Image Side - Always first in DOM, use order for positioning */}
            <div className={`${imagePosition === 'left' ? 'lg:order-1' : 'lg:order-2'}`}>
              <div className="relative w-full">
                {/* Decorative background elements */}
                <div 
                  className={`absolute w-full h-full rounded-xl opacity-12 ${imagePosition === 'left' ? '-top-2 -left-2' : '-top-2 -right-2'}`}
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                ></div>
                {/* Image container - Dynamic aspect ratio */}
                <div className={`relative rounded-xl overflow-hidden shadow-lg ring-1 ring-gray-200/50 ${getAspectClass()}`}>
                  <img 
                    src={image} 
                    alt={title || 'Section image'} 
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  {/* Subtle gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-black/10 via-transparent to-white/5"></div>
                </div>
              </div>
            </div>
            
            {/* Content Side */}
            <div className={`${imagePosition === 'left' ? 'lg:order-2 lg:pl-6' : 'lg:order-1 lg:pr-6'} space-y-2`}>
              {title && (
                <h2 className="font-semibold text-gray-900 leading-tight" style={{ fontSize: '1.125rem' }}>
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-gray-500 font-medium" style={{ fontSize: '1rem' }}>
                  {subtitle}
                </p>
              )}
              <div className="space-y-2">
                {paragraphs?.map((para, index) => (
                  <p key={index} className="text-gray-600 leading-relaxed" style={{ fontSize: '1rem' }}>
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            {title && (
              <h2 className={`font-semibold text-gray-900 mb-2 ${centered ? 'text-center' : ''}`} style={{ fontSize: '1.125rem' }}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className={`text-gray-500 mb-3 ${centered ? 'text-center' : ''}`} style={{ fontSize: '1rem' }}>
                {subtitle}
              </p>
            )}
            <div className="space-y-2">
              {paragraphs?.map((para, index) => (
                <p key={index} className={`text-gray-600 leading-relaxed ${centered ? 'text-center' : ''}`} style={{ fontSize: '1rem' }}>
                  {para}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// 2. Benefit Cards (3-column grid with icons)
const BenefitCards = ({ title, subtitle, cards, primaryColor }) => {
  return (
    <section className="bg-gray-50" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <h2 className="font-semibold text-gray-900 text-center mb-2" style={{ fontSize: '1.125rem' }}>
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-center text-gray-600 mb-6" style={{ fontSize: '1rem' }}>
            {subtitle}
          </p>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards?.map((card, index) => {
            const IconComponent = iconComponents[card.icon] || Shield;
            const iconBg = card.iconBg || `${primaryColor || '#10b981'}15`;
            const iconColor = card.iconColor || primaryColor || '#10b981';
            
            return (
              <div 
                key={index} 
                className="bg-white p-4 rounded-xl shadow-sm text-center hover:shadow-md transition-shadow"
              >
                <div 
                  className="flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-3"
                  style={{ backgroundColor: iconBg }}
                >
                  <IconComponent 
                    className="h-6 w-6" 
                    style={{ color: iconColor }} 
                  />
                </div>
                <h3 className="font-bold text-gray-900 mb-2" style={{ fontSize: '1rem' }}>
                  {card.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-2" style={{ fontSize: '0.90rem' }}>
                  {card.description}
                </p>
                {card.badge && (
                  <span 
                    className="inline-block px-2 py-0.5 text-xs font-medium rounded-full"
                    style={{ backgroundColor: `${iconColor}15`, color: iconColor }}
                  >
                    {card.badge}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// 3. Symptom Cards (2-column grid with checkmarks)
const SymptomCards = ({ title, subtitle, items, primaryColor }) => {
  const checkColor = primaryColor || '#14b8a6';
  
  return (
    <section className="bg-white" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <h2 className="font-bold text-gray-900 text-center mb-3" style={{ fontSize: '1.125rem' }}>
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-center text-gray-600 mb-8" style={{ fontSize: '1rem' }}>
            {subtitle}
          </p>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items?.map((item, index) => (
            <div 
              key={index}
              className="flex items-start gap-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div 
                className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full"
                style={{ backgroundColor: `${checkColor}20` }}
              >
                <CheckCircle 
                  className="h-5 w-5" 
                  style={{ color: checkColor }} 
                />
              </div>
              <div className="flex-1">
                <p className="text-gray-800 font-medium">{item.text}</p>
                {item.description && (
                  <p className="text-gray-600 text-sm mt-1">{item.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 4. Process Steps (numbered steps with large badges)
const ProcessSteps = ({ title, subtitle, steps, primaryColor }) => {
  const stepColor = primaryColor || '#14b8a6';
  
  return (
    <section className="bg-gray-50" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <h2 className="font-semibold text-gray-900 text-center mb-2" style={{ fontSize: '1.125rem' }}>
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-center text-gray-600 mb-8" style={{ fontSize: '1rem' }}>
            {subtitle}
          </p>
        )}
        
        <div className="space-y-3">
          {steps?.map((step, index) => (
            <div 
              key={index}
              className="flex items-start gap-4 p-4 bg-white rounded-xl border-l-3 hover:shadow-sm transition-shadow"
              style={{ borderColor: stepColor, borderLeftWidth: '3px' }}
            >
              <div 
                className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-semibold"
                style={{ backgroundColor: stepColor }}
              >
                {index + 1}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-0.5" style={{ fontSize: '1rem' }}>
                  {step.title}
                </h3>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 5. Info Box (highlighted important information)
const InfoBox = ({ title, content, boxType, icon }) => {
  const types = {
    warning: {
      bg: '#fffbeb',
      border: '#f59e0b',
      icon: AlertTriangle,
      iconColor: '#f59e0b'
    },
    info: {
      bg: '#eff6ff',
      border: '#3b82f6',
      icon: Info,
      iconColor: '#3b82f6'
    },
    important: {
      bg: '#fef2f2',
      border: '#ef4444',
      icon: Target,
      iconColor: '#ef4444'
    },
    success: {
      bg: '#f0fdf4',
      border: '#10b981',
      icon: CheckCircle,
      iconColor: '#10b981'
    }
  };
  
  const config = types[boxType] || types.info;
  const IconComponent = config.icon;
  
  return (
    <section className="py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          className="p-6 rounded-xl border-l-4"
          style={{ 
            backgroundColor: config.bg,
            borderColor: config.border
          }}
        >
          <div className="flex items-start gap-4">
            <IconComponent 
              className="h-6 w-6 flex-shrink-0 mt-0.5" 
              style={{ color: config.iconColor }}
            />
            <div className="flex-1">
              {title && (
                <h3 
                  className="font-bold mb-2"
                  style={{ color: config.iconColor, fontSize: '1rem' }}
                >
                  {title}
                </h3>
              )}
              <p className="text-gray-700 leading-relaxed" style={{ fontSize: '1rem' }}>
                {content}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// 6. Comparison Columns (side-by-side, e.g., Dogs vs Cats)
const ComparisonColumns = ({ title, subtitle, columns, primaryColor }) => {
  return (
    <section className="bg-white" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <h2 className="font-semibold text-gray-900 text-center mb-2" style={{ fontSize: '1.125rem' }}>
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-center text-gray-600 mb-8" style={{ fontSize: '1rem' }}>
            {subtitle}
          </p>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {columns?.map((column, colIndex) => {
            // Get icon component if available
            const IconComponent = column.icon ? iconComponents[column.icon] : null;
            
            return (
              <div key={colIndex} className="bg-gray-50 rounded-xl p-5">
                <h3 className="font-bold text-gray-900 mb-5 flex items-center gap-2" style={{ fontSize: '1rem' }}>
                  {IconComponent ? (
                    <span 
                      className="flex items-center justify-center w-8 h-8 rounded-lg"
                      style={{ backgroundColor: `${primaryColor || '#14b8a6'}15` }}
                    >
                      <IconComponent 
                        className="h-4 w-4" 
                        style={{ color: primaryColor || '#14b8a6' }} 
                      />
                    </span>
                  ) : column.icon && (
                    <span className="text-xl">{column.icon}</span>
                  )}
                  {column.title}
                </h3>
                
                <div className="space-y-3">
                  {column.items?.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      className="border-l-3 pl-3 py-1.5"
                      style={{ borderColor: primaryColor || '#14b8a6', borderLeftWidth: '3px' }}
                    >
                      <h4 className="font-medium text-gray-900 mb-0.5 text-sm">
                        {item.name}
                      </h4>
                      <p className="text-gray-600 text-xs leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// 7. Care Cards (3-column grid, simpler than benefit cards)
const CareCards = ({ title, subtitle, cards, primaryColor }) => {
  const accentColor = primaryColor || '#14b8a6';
  
  return (
    <section className="bg-gray-50" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <h2 className="font-bold text-gray-900 text-center mb-3" style={{ fontSize: '1.125rem' }}>
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-center text-gray-600 mb-8" style={{ fontSize: '1rem' }}>
            {subtitle}
          </p>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards?.map((card, index) => (
            <div 
              key={index}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
            >
              <h3 className="font-bold mb-3" style={{ color: accentColor, fontSize: '1rem' }}>
                {card.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" style={{ fontSize: '1rem' }}>
                {card.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 8. Summary Table
const SummaryTable = ({ title, rows, primaryColor }) => {
  const headerColor = primaryColor || '#14b8a6';
  
  return (
    <section className="bg-gray-50" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <h2 className="font-bold text-gray-900 text-center mb-6" style={{ fontSize: '1.125rem' }}>
            {title}
          </h2>
        )}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ backgroundColor: headerColor }}>
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold">Category</th>
                  <th className="px-6 py-4 text-left text-white font-semibold">Key Points</th>
                </tr>
              </thead>
              <tbody>
                {rows?.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 font-semibold text-gray-900">{row.category}</td>
                    <td className="px-6 py-4 text-gray-600">{row.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

// 9. Feature Grid (alternative to benefit cards with different layout)
const FeatureGrid = ({ title, subtitle, features, columns = 3, primaryColor }) => {
  const accentColor = primaryColor || '#14b8a6';
  const gridCols = columns === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-4';
  
  return (
    <section className="bg-white" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <h2 className="font-semibold text-gray-900 text-center mb-2" style={{ fontSize: '1.125rem' }}>
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-center text-gray-600 mb-6" style={{ fontSize: '1rem' }}>
            {subtitle}
          </p>
        )}
        
        <div className={`grid grid-cols-1 ${gridCols} gap-4`}>
          {features?.map((feature, index) => {
            const IconComponent = iconComponents[feature.icon] || CheckCircle;
            
            return (
              <div 
                key={index}
                className="p-4 rounded-xl"
                style={{ backgroundColor: `${accentColor}08` }}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg"
                    style={{ backgroundColor: `${accentColor}15` }}
                  >
                    <IconComponent 
                      className="h-4 w-4" 
                      style={{ color: accentColor }} 
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1" style={{ fontSize: '1rem' }}>{feature.title}</h3>
                    {feature.items ? (
                      <ul className="text-gray-600 space-y-0.5" style={{ fontSize: '0.90rem' }}>
                        {feature.items.map((item, i) => (
                          <li key={i}>• {item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600" style={{ fontSize: '0.90rem' }}>{feature.description}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// 10. FAQ Section (Expandable questions)
const FAQSection = ({ title, subtitle, faqs, primaryColor }) => {
  const [openIndex, setOpenIndex] = React.useState(null);
  const accentColor = primaryColor || '#14b8a6';
  
  return (
    <section className="bg-gray-50" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <h2 className="font-semibold text-gray-900 text-center mb-2" style={{ fontSize: '1.125rem' }}>
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-center text-gray-600 mb-6" style={{ fontSize: '1rem' }}>
            {subtitle}
          </p>
        )}
        
        <div className="space-y-3">
          {faqs?.map((faq, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              <button
                className="w-full px-5 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="font-medium text-gray-900 text-sm">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-5 pb-3">
                  <p className="text-gray-600 leading-relaxed text-sm">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 11. Stats Section (Key numbers/metrics)
const StatsSection = ({ title, stats, primaryColor }) => {
  const accentColor = primaryColor || '#14b8a6';
  
  return (
    <section className="bg-white" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <h2 className="font-semibold text-gray-900 text-center mb-6" style={{ fontSize: '1.125rem' }}>
            {title}
          </h2>
        )}
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats?.map((stat, index) => (
            <div key={index} className="text-center">
              <div 
                className="text-2xl font-bold mb-1"
                style={{ color: accentColor }}
              >
                {stat.value}
              </div>
              <div className="text-gray-600 text-xs">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 12. Bullet List Section (Simple list with icons)
const BulletList = ({ title, subtitle, items, icon = 'check-circle', primaryColor, backgroundColor = 'white' }) => {
  const IconComponent = iconComponents[icon] || CheckCircle;
  const checkColor = primaryColor || '#14b8a6';
  const bgClass = backgroundColor === 'gray' ? 'bg-gray-50' : 'bg-white';
  
  return (
    <section className={`${bgClass}`} style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {title && (
          <h2 className="font-bold text-gray-900 text-center mb-3" style={{ fontSize: '1.125rem' }}>
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-center text-gray-600 mb-8" style={{ fontSize: '1rem' }}>
            {subtitle}
          </p>
        )}
        
        <div className="space-y-3">
          {items?.map((item, index) => (
            <div 
              key={index}
              className="flex items-start gap-3"
            >
              <IconComponent 
                className="h-5 w-5 flex-shrink-0 mt-0.5" 
                style={{ color: checkColor }} 
              />
              <span className="text-gray-700">{typeof item === 'string' ? item : item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// 13. Image + Text Section (Split layout with image and text)
const ImageText = ({ title, subtitle, content, image, imageAlt, imagePosition = 'right', bullets, primaryColor, backgroundColor = 'white' }) => {
  const bgClass = backgroundColor === 'gray' ? 'bg-gray-50' : 'bg-white';
  const accentColor = primaryColor || '#14b8a6';
  const isImageLeft = imagePosition === 'left';
  
  return (
    <section className={`${bgClass}`} style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex flex-col ${isImageLeft ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 lg:gap-12 items-center`}>
          {/* Text Content */}
          <div className="flex-1 space-y-4">
            {title && (
              <h2 className="font-bold text-gray-900" style={{ fontSize: '1.125rem' }}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-lg text-gray-600 font-medium">
                {subtitle}
              </p>
            )}
            {content && (
              <div className="space-y-3">
                {(Array.isArray(content) ? content : [content]).map((para, idx) => (
                  <p key={idx} className="text-gray-600 leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            )}
            {bullets && bullets.length > 0 && (
              <ul className="space-y-2 mt-4">
                {bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle 
                      className="h-5 w-5 flex-shrink-0 mt-0.5" 
                      style={{ color: accentColor }}
                    />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          {/* Image */}
          <div className="flex-1 w-full">
            {image ? (
              <img 
                src={image} 
                alt={imageAlt || title || 'Section image'}
                className="w-full h-auto rounded-xl shadow-lg object-cover"
                style={{ maxHeight: '400px' }}
              />
            ) : (
              <div 
                className="w-full h-64 lg:h-80 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}15` }}
              >
                <span className="text-gray-400">Image placeholder</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Main Renderer
const StructuredContentRenderer = ({ content, primaryColor }) => {
  if (!content || !content.sections) {
    return null;
  }

  // Use CSS variable or fallback
  const effectiveColor = primaryColor || 'var(--theme-primary, #14b8a6)';

  return (
    <div className="structured-content">
      {content.sections.map((section, index) => {
        const key = `section-${index}`;
        
        switch (section.type) {
          case 'text-block':
            return <TextBlock key={key} {...section} />;
          
          case 'benefit-cards':
            return <BenefitCards key={key} {...section} primaryColor={effectiveColor} />;
          
          case 'symptom-cards':
            return <SymptomCards key={key} {...section} primaryColor={effectiveColor} />;
          
          case 'process-steps':
            return <ProcessSteps key={key} {...section} primaryColor={effectiveColor} />;
          
          case 'info-box':
            return <InfoBox key={key} {...section} />;
          
          case 'comparison-columns':
            return <ComparisonColumns key={key} {...section} primaryColor={effectiveColor} />;
          
          case 'care-cards':
            return <CareCards key={key} {...section} primaryColor={effectiveColor} />;
          
          case 'summary-table':
            return <SummaryTable key={key} {...section} primaryColor={effectiveColor} />;
          
          case 'feature-grid':
            return <FeatureGrid key={key} {...section} primaryColor={effectiveColor} />;
          
          case 'faq':
            return <FAQSection key={key} {...section} primaryColor={effectiveColor} />;
          
          case 'stats':
            return <StatsSection key={key} {...section} primaryColor={effectiveColor} />;
          
          case 'bullet-list':
            return <BulletList key={key} {...section} primaryColor={effectiveColor} />;
          
          case 'image-text':
            return <ImageText key={key} {...section} primaryColor={effectiveColor} />;
          
          default:
            console.warn(`Unknown section type: ${section.type}`);
            return null;
        }
      })}
    </div>
  );
};

export default StructuredContentRenderer;
